import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FaMapMarkerAlt, FaCalendarAlt, FaTicketAlt, FaPlayCircle } from 'react-icons/fa';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetLiveLinksByEventQuery } from '../../redux/api/apiSlice';

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

const EventCard = ({ event }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const t = useTranslation();

  const isSatsang = (event?.name || '').toLowerCase().includes('satsang');
  const { data: liveLinksData } = useGetLiveLinksByEventQuery(event?._id, {
    skip: !isSatsang || !event?._id,
  });
  const liveLinks = Array.isArray(liveLinksData) ? liveLinksData : [];

  const handleBooking = () => {
    if (isAuthenticated) {
      navigate(`/booking/${event._id}`);
    } else {
      navigate(`/login`, { state: { from: `/booking/${event._id}` } });
    }
  };

  const bookingStartAdjusted = new Date(event.bookingStartDate);
  bookingStartAdjusted.setHours(0, 1, 0, 0); // Set to 00:01:00.000

  const bookingEndAdjusted = new Date(event.bookingEndDate);
  bookingEndAdjusted.setDate(bookingEndAdjusted.getDate() - 1); // Subtract one day
  bookingEndAdjusted.setHours(23, 59, 59, 999); // Set to 23:59:59.999 of the adjusted day

  const now = new Date();

  // Logic to determine booking status
  const isManuallyClosed = event.isBookingOpen === false;
  const isDateWindowActive = now >= bookingStartAdjusted && now <= bookingEndAdjusted;

  const isBookingActive = isDateWindowActive && !isManuallyClosed;
  const isBookingNotYetOpen = now < bookingStartAdjusted;

  // Base classes for the button
  const baseButtonClasses = "w-full py-3 text-white font-semibold rounded-lg shadow-soft transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-primary/50";

  // Dynamic classes based on booking status
  const activeClasses = "bg-primaryDark hover:bg-highlight";
  const inactiveClasses = "bg-gray-400 hover:bg-gray-500 cursor-not-allowed"; // Changed to use gray colors for 'dull' look

  return (
    <motion.div
      className="bg-card p-6 rounded-2xl shadow-soft hover:shadow-accent transition-all duration-300 transform hover:-translate-y-0.5 flex flex-col h-full font-body"
      variants={itemVariants}
    >
      <h2 className="text-2xl font-bold font-heading text-primaryDark mb-2 border-b border-background pb-2">
        {event.name}
      </h2>
      <p className="text-gray-700 mb-4 flex-grow text-sm md:text-base">
        {event.description}
      </p>

      {/* Details Section */}
      <div className="text-sm text-gray-700 space-y-2 mb-6">
        <p className="flex items-center space-x-2">
          <FaMapMarkerAlt className="text-primary flex-shrink-0" />
          <span><strong>{t.events.card.location}:</strong> {event.location}</span>
        </p>
        <p className="flex items-center space-x-2">
          <FaCalendarAlt className="text-primary flex-shrink-0" />
          <span><strong>{t.events.card.dates}:</strong> {new Date(event.startDate).toLocaleDateString('en-GB')} - {new Date(event.endDate).toLocaleDateString('en-GB')}</span>
        </p>
        <p className="flex items-center space-x-2">
          <FaTicketAlt className="text-primary flex-shrink-0" />
          <span><strong>{t.events.card.bookingWindow}:</strong> {new Date(event.bookingStartDate).toLocaleDateString('en-GB')} - {new Date(event.bookingEndDate).toLocaleDateString('en-GB')}</span>
        </p>
      </div>

      {/* Live Links Section */}
      {(event.name || '').toLowerCase().includes('satsang') && liveLinks.length > 0 && (
        <div className="mb-6 p-3 bg-background/50 rounded-lg border border-background">
          <h4 className="font-semibold font-heading text-primaryDark mb-2 flex items-center space-x-1">
            <FaPlayCircle className="text-primary" /> <span>{t.events.card.liveLinks}:</span>
          </h4>
          <ul className="space-y-1">
            {liveLinks.map(link => (
              <li key={link._id} className="text-highlight text-sm">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline font-medium"
                >
                  {link.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Button */}
      <div className="mt-auto">
        {isBookingActive ? (
          <button
            onClick={handleBooking}
            className={`${baseButtonClasses} ${activeClasses}`}
          >
            {t.events.card.requestBooking}
          </button>
        ) : isManuallyClosed ? (
          <div className="w-full">
            <button
              className={`${baseButtonClasses} ${inactiveClasses}`}
              disabled
            >
              {t.events.card.bookingClosed}
            </button>
            {event.bookingClosedMessage && (
              <p className="text-red-500 text-sm mt-2 text-center font-medium bg-red-50 p-2 rounded border border-red-100">
                {event.bookingClosedMessage}
              </p>
            )}
          </div>
        ) : isBookingNotYetOpen ? (
          <button
            className={`${baseButtonClasses} ${inactiveClasses}`}
            disabled
          >
            {t.events.card.bookingNotStarted}
          </button>
        ) : (
          <button
            className={`${baseButtonClasses} ${inactiveClasses}`}
            disabled
          >
            {t.events.card.bookingClosed}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default EventCard;